import React, { useState, useEffect } from 'react'
import { Plus, FileText, Upload } from 'lucide-react'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CreateTestCaseModal } from './CreateTestCaseModal'
import { EditTestCaseModal } from './EditTestCaseModal'
import { BulkImportTestCasesModal } from './BulkImportTestCasesModal'
import { SortableTestCaseItem } from './SortableTestCaseItem'
import { evaluationService } from '@/services/evaluationService'
import { useToast } from '@/hooks/useToast'
import type { TestSetWithDetails, TestCaseWithEvaluations } from '@shared/types/evaluation'

interface TestCaseManagerProps {
  testSetId: string
  testSet: TestSetWithDetails
  onRefresh?: () => void
}

export const TestCaseManager: React.FC<TestCaseManagerProps> = ({ testSetId, testSet, onRefresh }) => {
  // Extract test cases from the test set data and maintain local state for reordering
  const initialTestCases: TestCaseWithEvaluations[] = testSet.testCases?.map(tc => tc.testCase) || []
  const [testCases, setTestCases] = useState<TestCaseWithEvaluations[]>(initialTestCases)
  const [isReordering, setIsReordering] = useState(false)
  const { toast } = useToast()
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showBulkImportModal, setShowBulkImportModal] = useState(false)
  const [selectedTestCase, setSelectedTestCase] = useState<TestCaseWithEvaluations | null>(null)

  // Update local state when testSet changes
  useEffect(() => {
    const updatedTestCases = testSet.testCases?.map(tc => tc.testCase) || []
    setTestCases(updatedTestCases)
  }, [testSet])

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = async (event: any) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = testCases.findIndex(item => item.id === active.id)
      const newIndex = testCases.findIndex(item => item.id === over.id)
      
      const newTestCases = arrayMove(testCases, oldIndex, newIndex)
      setTestCases(newTestCases)
      
      // Save the new order to the backend
      setIsReordering(true)
      try {
        await evaluationService.reorderTestCases(testSetId, {
          testCaseIds: newTestCases.map(tc => tc.id)
        })
        toast.success("Test cases reordered successfully")
      } catch (error) {
        // Revert the order on error
        setTestCases(testCases)
        toast.error("Failed to reorder test cases")
      } finally {
        setIsReordering(false)
      }
    }
  }

  const handleEditTestCase = (testCase: TestCaseWithEvaluations) => {
    setSelectedTestCase(testCase)
    setShowEditModal(true)
  }

  const handleDeleteTestCase = (testCase: TestCaseWithEvaluations) => {
    // TODO: Implement delete functionality
    console.log('Delete test case:', testCase.id)
  }

  const handleModalSuccess = () => {
    // Refresh the test set data
    onRefresh?.()
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Test Cases</h3>
          <p className="text-sm text-muted-foreground">
            Manage test cases for evaluation runs
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => setShowBulkImportModal(true)}
          >
            <Upload className="h-4 w-4" />
            Bulk Import
          </Button>
          <Button 
            className="gap-2"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="h-4 w-4" />
            Add Test Case
          </Button>
        </div>
      </div>

      {/* Test Cases */}
      {testCases.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Test Cases</h3>
              <p className="text-muted-foreground mb-4">
                Get started by adding your first test case to this test set.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="gap-2"
                  onClick={() => setShowBulkImportModal(true)}
                >
                  <Upload className="h-4 w-4" />
                  Bulk Import
                </Button>
                <Button 
                  className="gap-2"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus className="h-4 w-4" />
                  Add Your First Test Case
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {isReordering && (
            <div className="text-sm text-muted-foreground mb-2">
              Saving new order...
            </div>
          )}
          
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext 
              items={testCases.map(tc => tc.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {testCases.map((testCase: TestCaseWithEvaluations, index: number) => (
                  <SortableTestCaseItem
                    key={testCase.id}
                    testCase={testCase}
                    index={index}
                    onEdit={handleEditTestCase}
                    onDelete={handleDeleteTestCase}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Modals */}
      <CreateTestCaseModal
        testSetId={testSetId}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleModalSuccess}
      />

      <EditTestCaseModal
        testSetId={testSetId}
        testCase={selectedTestCase}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedTestCase(null)
        }}
        onSuccess={handleModalSuccess}
      />

      <BulkImportTestCasesModal
        testSetId={testSetId}
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  )
}